/**
 * Product Detail — Individual product page.
 * Uses BreadcrumbNav, QuantitySelector, PriceDisplay, and ProductGrid.
 */

import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState, useMemo } from "react";
import {
  Star,
  ShieldCheck,
  Truck,
  RotateCcw,
  Check,
  Heart,
  Trash2,
  AlertCircle,
  X,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { Reveal } from "@/components/common/reveal";
import { BreadcrumbNav } from "@/components/common/breadcrumb-nav";
import { QuantitySelector } from "@/components/forms/quantity-selector";
import { PriceDisplay, formatPrice } from "@/components/forms/price-display";
import { ProductCard } from "@/features/products/components/product-card";
import { ProductReviewsSection } from "@/components/products/ProductReviewsSection";
import { useCart } from "@/features/cart/store/cart-store";
import { useCurrentUser } from "@/hooks/use-auth";
import {
  products,
  getLiveProductById,
  getCatalogProductById,
  parseProductDimensions,
  getProductBgImagePath,
  cleanSeoKeywords,
  type Product,
} from "@/lib/products";
import { useLiveProduct, useLiveProducts } from "@/features/products/hooks/use-live-products";
import { deleteProductFromSupabase } from "@/features/admin/products/store/admin-products-store";
import { getProductSchema, safeJsonLd } from "@/lib/seo";
import { siteConfig } from "@/config/site";
import { RazorpayAddressModal } from "@/components/checkout/RazorpayAddressModal";
import { BundleOfferSection } from "@/components/products/BundleOfferSection";
import { ProductDescription } from "@/components/products/ProductDescription";
import {
  trackMetaViewContent,
  trackMetaAddToCart,
  trackMetaInitiateCheckout,
} from "@/lib/meta-pixel";

export const Route = createFileRoute("/product/$id")({
  head: ({ params }) => {
    // Catalogue lookup, NOT the live one: head() runs twice -- once in the prerender and
    // again during hydration -- and must produce byte-identical output both times or React
    // rejects the whole head as a mismatch (#418). getLiveProductById answers from
    // localStorage in the browser and from the seed catalogue on the server, so it cannot
    // satisfy that. See getCatalogProductById in @/lib/products.
    const item = getCatalogProductById(params.id);
    const fallbackTitle = params.id
      ? decodeURIComponent(params.id)
          .replace(/[_-]+/g, " ")
          .replace(/\b\w/g, (c) => c.toUpperCase())
      : "Product Details";
    const pageTitle =
      item?.seoTitle ||
      (item ? `${item.name} | ${siteConfig.name}` : `${fallbackTitle} | ${siteConfig.name}`);
    const rawDesc = cleanSeoKeywords(item?.seoDescription || item?.description || item?.note || "");
    const firstPara = rawDesc.split("\n\n")[0] || rawDesc.split("\n")[0] || "";
    const pageDesc = (
      firstPara ||
      `Shop premium ${item?.name || fallbackTitle} at Aster Decorz. Artificial plants with lifelike foliage, included luxury planter, and zero maintenance.`
    )
      .replace(/[*#•–—]/g, "")
      .replace(/[\r\n]+/g, " ")
      .replace(/\s+/g, " ")
      .trim()
      .slice(0, 160);
    const canonicalUrl = `${siteConfig.url}/product/${item?.slug || params.id}`;
    // Product images are stored as site-relative paths ("/plantpicture/p1/5.webp").
    // Open Graph and Twitter both require an absolute URL -- crawlers do not resolve a
    // relative one against the page, they discard it, which is why shared product links
    // previewed with no image.
    const shareImage = item?.image
      ? item.image.startsWith("http")
        ? item.image
        : `${siteConfig.url}${item.image}`
      : `${siteConfig.url}/plantpicture/p1/5.webp`;

    return {
      meta: [
        { title: pageTitle },
        { name: "description", content: pageDesc },
        {
          name: "keywords",
          content: `${item?.name || fallbackTitle}, ${item?.category || "Artificial Plants"}, artificial plants, faux greenery, home decor, aster decorz`,
        },
        { property: "og:title", content: pageTitle },
        { property: "og:description", content: pageDesc },
        { property: "og:image", content: shareImage },
        // Overrides the root default, which describes the brand card rather than this
        // photo. No width/height: product artwork is square and comes in several sizes.
        { property: "og:image:alt", content: item?.name || fallbackTitle },
        { property: "og:url", content: canonicalUrl },
        { property: "og:type", content: "product" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: pageTitle },
        { name: "twitter:description", content: pageDesc },
        { name: "twitter:image", content: shareImage },
      ],
      links: [{ rel: "canonical", href: canonicalUrl }],
    };
  },
  component: ProductDetail,
});

function ProductDetail() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const { add } = useCart();
  const { isAdmin, user, isAuthenticated } = useCurrentUser();
  const { product: liveProduct, loading } = useLiveProduct(id);
  const { products: allLiveProducts } = useLiveProducts();

  const product = liveProduct || getLiveProductById(id);

  const bgPath = product ? getProductBgImagePath(product.id) : undefined;
  const displayGallery = useMemo(() => {
    if (!product) return [];
    return Array.from(
      new Set([
        product.image,
        product.hoverImage,
        bgPath,
        ...(Array.isArray(product.gallery) ? product.gallery : []),
      ]),
    ).filter(
      (img): img is string => typeof img === "string" && Boolean(img) && !img.includes("posters"),
    );
  }, [product?.id, product?.image, product?.hoverImage, product?.gallery, bgPath]);

  const [activeImg, setActiveImg] = useState<string>(
    () => displayGallery[0] || product?.image || "",
  );

  useEffect(() => {
    if (product) {
      setActiveImg(displayGallery[0] || product.image);
      if (product.variants?.options) {
        setSelectedVariant(product.variants.options[0]);
      }
    }
  }, [product?.id, product?.image, displayGallery]);

  const [selectedVariant, setSelectedVariant] = useState(product?.variants?.options[0] || "");

  // Keep variant state in sync if product changes
  const currentVariant = product?.variants?.options.includes(selectedVariant)
    ? selectedVariant
    : product?.variants?.options[0] || "";

  const [qty, setQty] = useState(1);
  const [added, setAdded] = useState(false);
  const [openTab, setOpenTab] = useState<string | null>("details");
  const [addressModalOpen, setAddressModalOpen] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  useEffect(() => {
    if (!lightboxOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setLightboxOpen(false);
      if (displayGallery.length <= 1) return;
      if (e.key === "ArrowLeft") {
        const idx = displayGallery.indexOf(activeImg);
        const prev = idx <= 0 ? displayGallery.length - 1 : idx - 1;
        setActiveImg(displayGallery[prev]);
      } else if (e.key === "ArrowRight") {
        const idx = displayGallery.indexOf(activeImg);
        const next = idx >= displayGallery.length - 1 ? 0 : idx + 1;
        setActiveImg(displayGallery[next]);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [lightboxOpen, displayGallery, activeImg]);

  // Sync client-side document title with the actual loaded product
  useEffect(() => {
    if (product?.name) {
      document.title = product.seoTitle || `${product.name} | ${siteConfig.name}`;
      trackMetaViewContent({
        id: product.id,
        name: product.name,
        price: product.price,
        category: product.category,
      });
    }
  }, [product?.name, product?.seoTitle, product?.id, product?.price, product?.category]);

  // Ensure the product detail page always starts at the top (preventing scroll restore jumps into footer on refresh)
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" as ScrollBehavior });
  }, [id]);

  const relatedProducts = useMemo(() => {
    if (!product) return [];
    const pool = (allLiveProducts.length > 0 ? allLiveProducts : products).filter(
      (p) => p.id !== product.id,
    );
    const sameCat = pool.filter(
      (p) => (p.category || "").toLowerCase() === (product.category || "").toLowerCase(),
    );
    const otherCat = pool.filter(
      (p) => (p.category || "").toLowerCase() !== (product.category || "").toLowerCase(),
    );
    return [...sameCat, ...otherCat].slice(0, 3);
  }, [allLiveProducts, product?.id, product?.category]);

  if (!product && loading) {
    return <ProductDetailSkeleton id={id} />;
  }

  if (!product) {
    return <ProductNotFound id={id} />;
  }

  const productSchema = getProductSchema(product);
  const specs = parseProductDimensions(product);
  const displayDimensions =
    product.dimensions && !product.dimensions.toLowerCase().includes("standard")
      ? product.dimensions
      : specs.dimensions;
  const displayLength = product.lengthCm ?? specs.lengthCm;
  const displayWidth = product.widthCm ?? specs.widthCm;
  const displayHeight = product.heightCm ?? specs.heightCm;
  const displaySpread = product.foliageSpread || specs.foliageSpread;
  const displayWeight = product.weight ?? specs.weight;
  const displayPot = product.potDetails || specs.potDetails;
  const displayCare = product.careInstructions || specs.careInstructions;
  const displayMaterial = product.foliageMaterial || specs.foliageMaterial;
  const displayTrunk = product.trunkType || specs.trunkType;
  const displayMoss = product.mossTopping || specs.mossTopping;
  const displayUsage = product.usageType || specs.usageType;

  const liveStock = (product as { stock?: number }).stock;
  const isOutOfStock =
    product.inStock === false ||
    (product.stockQuantity !== undefined && product.stockQuantity <= 0) ||
    (liveStock !== undefined && liveStock <= 0);

  const handleAddToCart = () => {
    if (isOutOfStock) return;
    add(product, qty, product.variants ? currentVariant : undefined);
    trackMetaAddToCart({
      id: product.id,
      name: product.name,
      price: product.price,
      qty,
    });
    setAdded(true);
    setTimeout(() => setAdded(false), 2500);
  };

  const handleBuyNow = () => {
    if (isOutOfStock) return;
    trackMetaInitiateCheckout({
      items: [{ id: product.id, name: product.name, price: product.price, qty }],
      total: product.price * qty,
    });
    setAddressModalOpen(true);
  };

  const handleDeleteProduct = async () => {
    if (!confirm(`Are you sure you want to delete "${product.name}" from your catalog?`)) return;
    try {
      await deleteProductFromSupabase(product.id);
      alert(`"${product.name}" has been deleted successfully.`);
      navigate({ to: "/shop" });
    } catch (err: unknown) {
      const message = (err as { message?: string }).message;
      alert(`Failed to delete: ${message || err}`);
    }
  };

  return (
    <div className="section-container pb-32 pt-24 sm:pt-28 md:pt-32">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: safeJsonLd(productSchema) }}
      />

      {/* Main product layout */}
      <div className="grid gap-6 lg:grid-cols-12 lg:gap-10 items-start">
        {/* Left: Image gallery */}
        <div className="lg:col-span-7 min-w-0">
          <div className="flex gap-3 md:gap-4 items-start">
            {/* Side Thumbnails Bar (Vertical on all devices) */}
            <div className="flex flex-col gap-2 overflow-y-auto max-h-[250px] sm:max-h-[350px] md:max-h-[460px] shrink-0 w-12 sm:w-14 md:w-16 lg:w-20 scrollbar-none order-1">
              {displayGallery.map((img, i) => (
                <button
                  key={img}
                  onClick={() => setActiveImg(img)}
                  className={`relative aspect-[4/5] w-full shrink-0 overflow-hidden rounded-xl border-2 transition-[color,background-color,border-color,box-shadow,transform,opacity] ${
                    activeImg === img
                      ? "border-emerald-800 ring-2 ring-emerald-800/20 scale-[1.02]"
                      : "border-stone-200 opacity-70 hover:opacity-100"
                  }`}
                  aria-label={`View image ${i + 1}`}
                >
                  <img
                    src={img}
                    alt={`${product.name} — view ${i + 1}`}
                    loading="lazy"
                    className="h-full w-full object-cover"
                  />
                </button>
              ))}
            </div>

            {/* Main Image Container */}
            <div
              className="relative h-[250px] sm:h-[350px] md:h-[460px] flex-1 w-full overflow-hidden rounded-2xl bg-stone border border-stone-200/60 shadow-sm order-2 cursor-zoom-in"
              onClick={() => setLightboxOpen(true)}
            >
              <img
                src={activeImg}
                alt={product.name}
                decoding="async"
                width={1200}
                height={1500}
                className={`h-full w-full object-cover transition-opacity duration-300 ${
                  isOutOfStock ? "grayscale-30 opacity-80" : ""
                }`}
                fetchPriority="high"
              />

              {/* Badges Overlay */}
              <div className="absolute left-3 top-3 z-10 flex flex-col gap-2 pointer-events-none">
                {isOutOfStock ? (
                  <span className="rounded-lg bg-rose-900/90 px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-rose-100 backdrop-blur shadow-sm border border-rose-700/50">
                    Out of Stock
                  </span>
                ) : (
                  product.badge && (
                    <span className="rounded-lg bg-stone-900/90 px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-white backdrop-blur shadow-sm">
                      {product.badge}
                    </span>
                  )
                )}
              </div>

              {/* Floating Wishlist Heart Button over product picture */}
              <button
                aria-label="Add to wishlist"
                className="absolute right-3 top-3 grid h-9 w-9 place-items-center rounded-full bg-white/90 text-stone-700 shadow-md backdrop-blur transition-[color,background-color,border-color,box-shadow,transform,opacity] duration-200 hover:bg-white hover:text-emerald-800 hover:scale-110 active:scale-95"
              >
                <Heart className="h-4 w-4" strokeWidth={1.75} />
              </button>
            </div>
          </div>

          {/* Bundle & Save — sits directly under the product image */}
          <BundleOfferSection
            currentProduct={product}
            allProducts={allLiveProducts.length > 0 ? allLiveProducts : products}
          />
        </div>

        {/* Right: Product Buy Column */}
        <div className="lg:col-span-5 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <span className="eyebrow text-xs sm:text-sm font-bold tracking-widest text-emerald-800 uppercase">
              {product.category}
            </span>
            {isOutOfStock && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-rose-100 text-rose-800 dark:bg-rose-950 dark:text-rose-300 border border-rose-200 dark:border-rose-900">
                <AlertCircle className="h-3.5 w-3.5" /> Out of Stock
              </span>
            )}
          </div>
          <h1 className="mt-1.5 font-display text-2xl sm:text-3xl lg:text-4xl font-bold text-foreground leading-tight">
            {product.name}
          </h1>

          {/* Price & rating */}
          <div className="mt-3 flex flex-wrap items-center justify-between gap-2 border-b border-stone-200 pb-4">
            <PriceDisplay amount={product.price} compareAt={product.salePrice} size="lg" />
            <a
              href="#customer-reviews"
              className="flex items-center gap-1.5 text-xs sm:text-sm font-semibold text-foreground/80 hover:text-emerald-800 transition-colors"
            >
              <div className="flex items-center text-amber-500">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star key={i} className="h-4 w-4 fill-current" />
                ))}
              </div>
              <span className="underline decoration-stone-300 underline-offset-2">
                ({product.reviewsCount} reviews)
              </span>
            </a>
          </div>

          {/* Dynamic Variant Selector - Only shown if product has variants */}
          {product.variants && product.variants.options.length > 0 && (
            <div className="mt-6">
              <label className="eyebrow block text-xs sm:text-sm font-bold tracking-wider text-foreground uppercase">
                {product.variants.label} —{" "}
                <span className="font-bold text-emerald-800">{currentVariant}</span>
              </label>
              <div className="mt-2 flex flex-wrap gap-2" role="radiogroup">
                {product.variants.options.map((option, index) => (
                  <button
                    key={option}
                    role="radio"
                    aria-checked={currentVariant === option}
                    onClick={() => {
                      setSelectedVariant(option);
                      if (displayGallery[index]) {
                        setActiveImg(displayGallery[index]);
                      }
                    }}
                    className={`min-h-10 px-4 text-xs sm:text-sm tracking-[0.08em] uppercase transition-colors rounded-xl border font-semibold ${
                      currentVariant === option
                        ? "border-emerald-800 bg-emerald-900 text-white font-bold shadow-xs"
                        : "border-stone-300 text-stone-800 hover:border-stone-900 bg-background"
                    }`}
                  >
                    {option}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Desktop & Tablet Center Buy & Add to Bag Actions */}
          <div className="mt-6 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
            <div
              className={`shrink-0 flex items-center ${isOutOfStock ? "opacity-50 pointer-events-none" : ""}`}
            >
              <QuantitySelector value={qty} onChange={setQty} />
            </div>

            <button
              type="button"
              disabled={isOutOfStock}
              onClick={handleAddToCart}
              className={`flex-1 min-h-[50px] whitespace-nowrap px-5 text-xs sm:text-sm tracking-[0.12em] font-bold uppercase rounded-xl transition-[color,background-color,border-color,box-shadow,transform,opacity] duration-200 shadow-md border flex items-center justify-center ${
                isOutOfStock
                  ? "bg-stone-200 text-stone-500 border-stone-300 dark:bg-stone-800 dark:text-stone-500 dark:border-stone-700 cursor-not-allowed opacity-75"
                  : "bg-stone-950 hover:bg-stone-900 text-white border-stone-800 cursor-pointer active:scale-[0.98]"
              }`}
            >
              {isOutOfStock ? (
                <span>OUT OF STOCK</span>
              ) : added ? (
                <span className="flex items-center justify-center gap-2 text-emerald-400 whitespace-nowrap font-bold">
                  <Check className="h-4 w-4 text-emerald-400" /> ADDED TO BAG
                </span>
              ) : (
                <span className="whitespace-nowrap">ADD TO BAG — ₹{product.price * qty}</span>
              )}
            </button>

            <button
              type="button"
              disabled={isOutOfStock}
              onClick={handleBuyNow}
              className={`flex-1 min-h-[50px] whitespace-nowrap px-5 text-xs sm:text-sm tracking-[0.12em] font-bold uppercase rounded-xl transition-[color,background-color,border-color,box-shadow,transform,opacity] duration-200 shadow-md border flex items-center justify-center ${
                isOutOfStock
                  ? "bg-stone-200 text-stone-500 border-stone-300 dark:bg-stone-800 dark:text-stone-500 dark:border-stone-700 cursor-not-allowed opacity-75"
                  : "bg-emerald-800 hover:bg-emerald-700 text-white border-emerald-700 cursor-pointer active:scale-[0.98]"
              }`}
            >
              {isOutOfStock ? "SOLD OUT" : "BUY NOW"}
            </button>
          </div>

          {/* Accordion sections */}
          <div className="mt-8 divide-y border-b">
            {[
              {
                id: "details",
                title: "Dimensions & Specifications",
                content: (
                  <div className="space-y-2.5">
                    {displayDimensions && (
                      <p className="font-semibold text-foreground/90">{displayDimensions}</p>
                    )}
                    {(displayLength || displayWidth || displayHeight) && (
                      <p className="text-xs text-muted-foreground">
                        <span className="font-bold text-foreground">Physical Dimensions:</span>{" "}
                        {[
                          displayLength ? `L: ${displayLength} cm` : null,
                          displayWidth ? `W: ${displayWidth} cm` : null,
                          displayHeight ? `H: ${displayHeight} cm` : null,
                        ]
                          .filter(Boolean)
                          .join(" × ")}
                      </p>
                    )}
                    {displaySpread && (
                      <p className="text-xs text-muted-foreground">
                        <span className="font-bold text-foreground">Canopy Spread:</span>{" "}
                        {displaySpread}
                      </p>
                    )}
                    {displayWeight && (
                      <p className="text-xs text-muted-foreground">
                        <span className="font-bold text-foreground">Gross Weight:</span>{" "}
                        {displayWeight} kg
                      </p>
                    )}
                  </div>
                ),
              },
              {
                id: "pot",
                title: "Planter & Botanical Materials",
                content: (
                  <div className="space-y-2.5">
                    {displayPot && <p className="font-semibold text-foreground/90">{displayPot}</p>}
                    {displayMaterial && (
                      <p className="text-xs text-muted-foreground">
                        <span className="font-bold text-foreground">Foliage Material:</span>{" "}
                        {displayMaterial}
                      </p>
                    )}
                    {displayTrunk && (
                      <p className="text-xs text-muted-foreground">
                        <span className="font-bold text-foreground">Trunk & Structure:</span>{" "}
                        {displayTrunk}
                      </p>
                    )}
                    {displayMoss && (
                      <p className="text-xs text-muted-foreground">
                        <span className="font-bold text-foreground">Base Dressing:</span>{" "}
                        {displayMoss}
                      </p>
                    )}
                  </div>
                ),
              },
              {
                id: "care",
                title: "Care & Maintenance",
                content: (
                  <div className="space-y-2.5">
                    {displayCare && (
                      <p className="font-semibold text-foreground/90">{displayCare}</p>
                    )}
                    {displayUsage && (
                      <p className="text-xs text-muted-foreground">
                        <span className="font-bold text-foreground">Placement & Environment:</span>{" "}
                        {displayUsage}
                      </p>
                    )}
                  </div>
                ),
              },
              {
                id: "shipping",
                title: "Shipping & Returns",
                content: (
                  <p className="font-medium text-foreground/90">
                    Shipped in reinforced protective crates. Complimentary returns within{" "}
                    {siteConfig.shipping.returnDays} days of arrival.
                  </p>
                ),
              },
              {
                id: "reviews-acc",
                title: `Customer Reviews (${product.reviewsCount})`,
                content: (
                  <p className="font-medium text-foreground/90">
                    Rated {product.rating || 4.9} out of 5 stars by verified buyers. Scroll down to
                    read and post product feedback.
                  </p>
                ),
              },
            ].map((acc) => (
              <div key={acc.id} className="py-4">
                <button
                  onClick={() => setOpenTab(openTab === acc.id ? null : acc.id)}
                  className="flex w-full items-center justify-between text-left text-xs sm:text-sm font-bold tracking-[0.12em] uppercase text-foreground py-1"
                  aria-expanded={openTab === acc.id}
                  aria-controls={`panel-${acc.id}`}
                >
                  <span>{acc.title}</span>
                  <span className="text-xl font-bold" aria-hidden="true">
                    {openTab === acc.id ? "−" : "+"}
                  </span>
                </button>
                {openTab === acc.id && (
                  <div
                    id={`panel-${acc.id}`}
                    className="mt-3 text-base font-medium leading-relaxed text-foreground/90 border-l-2 border-emerald-800/40 pl-3 py-1"
                  >
                    {acc.content}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Product Description */}
          {product.description && (
            <div className="mt-8">
              <h3 className="eyebrow text-xs sm:text-sm font-bold tracking-wider text-foreground uppercase mb-3">
                Product Description
              </h3>
              <ProductDescription description={product.description} />
            </div>
          )}
        </div>
      </div>

      {/* Related products */}
      <section className="mt-20 pt-10">
        <Reveal>
          <p className="eyebrow">Complete the look</p>
          <h2 className="mt-4">Pairs well with</h2>
        </Reveal>
        <div className="mt-8 md:mt-12 grid gap-x-4 md:gap-x-6 gap-y-8 md:gap-y-16 grid-cols-2 lg:grid-cols-3">
          {relatedProducts.map((p, i) => (
            <Reveal key={p.id} delay={i * 80}>
              <ProductCard product={p} />
            </Reveal>
          ))}
        </div>
      </section>

      {/* Customer Reviews Section */}
      <div className="mt-28 border-t pt-20">
        <Reveal>
          <ProductReviewsSection product={product} />
        </Reveal>
      </div>

      {/* Sticky Bottom Action Bar — Visible ONLY on Mobile */}
      <div
        className="fixed bottom-0 inset-x-0 z-30 md:hidden border-t border-stone-300/80 bg-background/95 px-3 sm:px-4 pt-3 backdrop-blur-md shadow-2xl"
        style={{ paddingBottom: "calc(0.75rem + env(safe-area-inset-bottom))" }}
      >
        <div className="mx-auto flex max-w-3xl items-center justify-between gap-2 sm:gap-3">
          <div
            className={`hidden sm:block ${isOutOfStock ? "opacity-50 pointer-events-none" : ""}`}
          >
            <QuantitySelector value={qty} onChange={setQty} />
          </div>

          <button
            disabled={isOutOfStock}
            onClick={handleAddToCart}
            className={`flex-1 min-h-[48px] px-3 sm:px-5 text-[11px] sm:text-xs tracking-[0.14em] font-bold uppercase rounded-xl transition-[color,background-color,border-color,box-shadow,transform,opacity] duration-200 shadow-md border ${
              isOutOfStock
                ? "bg-stone-200 text-stone-500 border-stone-300 dark:bg-stone-800 dark:text-stone-500 dark:border-stone-700 cursor-not-allowed opacity-75"
                : "bg-stone-950 hover:bg-stone-900 text-white border-stone-800 cursor-pointer active:scale-[0.98]"
            }`}
          >
            {isOutOfStock ? (
              <span>OUT OF STOCK</span>
            ) : added ? (
              <span className="flex items-center justify-center gap-1 text-emerald-400">
                <Check className="h-4 w-4 text-emerald-400" /> ADDED
              </span>
            ) : (
              <span>ADD TO BAG — ₹{product.price * qty}</span>
            )}
          </button>

          <button
            disabled={isOutOfStock}
            onClick={handleBuyNow}
            className={`flex-1 min-h-[48px] px-3 sm:px-5 text-[11px] sm:text-xs tracking-[0.14em] font-bold uppercase rounded-xl transition-[color,background-color,border-color,box-shadow,transform,opacity] duration-200 shadow-md border ${
              isOutOfStock
                ? "bg-stone-200 text-stone-500 border-stone-300 dark:bg-stone-800 dark:text-stone-500 dark:border-stone-700 cursor-not-allowed opacity-75"
                : "bg-emerald-800 hover:bg-emerald-700 text-white border-emerald-700 cursor-pointer active:scale-[0.98]"
            }`}
          >
            {isOutOfStock ? "SOLD OUT" : "BUY NOW"}
          </button>
        </div>
      </div>

      {/* 1-Click Razorpay Address & Payment Modal */}
      <RazorpayAddressModal
        open={addressModalOpen}
        onClose={() => setAddressModalOpen(false)}
        items={[
          {
            id: product.id,
            name: product.name,
            price: product.price,
            qty: qty,
            image: product.image,
          },
        ]}
        totalAmount={product.price * qty}
      />

      {/* Lightbox Modal */}
      {lightboxOpen && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/95 backdrop-blur-md p-4 sm:p-8 cursor-zoom-out select-none"
          onClick={() => setLightboxOpen(false)}
        >
          <button
            className="absolute top-4 right-4 sm:top-6 sm:right-6 grid h-10 w-10 place-items-center rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors z-20"
            onClick={(e) => {
              e.stopPropagation();
              setLightboxOpen(false);
            }}
            aria-label="Close fullscreen image"
          >
            <X className="h-5 w-5" />
          </button>

          {displayGallery.length > 1 && (
            <>
              <button
                className="absolute left-3 sm:left-6 top-1/2 -translate-y-1/2 grid h-11 w-11 place-items-center rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors z-20"
                onClick={(e) => {
                  e.stopPropagation();
                  const idx = displayGallery.indexOf(activeImg);
                  const prev = idx <= 0 ? displayGallery.length - 1 : idx - 1;
                  setActiveImg(displayGallery[prev]);
                }}
                aria-label="Previous image"
              >
                <ChevronLeft className="h-6 w-6" />
              </button>

              <button
                className="absolute right-3 sm:right-6 top-1/2 -translate-y-1/2 grid h-11 w-11 place-items-center rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors z-20"
                onClick={(e) => {
                  e.stopPropagation();
                  const idx = displayGallery.indexOf(activeImg);
                  const next = idx >= displayGallery.length - 1 ? 0 : idx + 1;
                  setActiveImg(displayGallery[next]);
                }}
                aria-label="Next image"
              >
                <ChevronRight className="h-6 w-6" />
              </button>

              {/* Bottom Thumbnail indicator in lightbox */}
              <div
                className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2 px-3 py-1.5 rounded-full bg-black/60 backdrop-blur-md z-20 border border-white/10"
                onClick={(e) => e.stopPropagation()}
              >
                {displayGallery.map((img, i) => (
                  <button
                    key={img}
                    onClick={() => setActiveImg(img)}
                    className={`h-2.5 rounded-full transition-all ${
                      activeImg === img ? "w-6 bg-white" : "w-2.5 bg-white/40 hover:bg-white/70"
                    }`}
                    aria-label={`Jump to image ${i + 1}`}
                  />
                ))}
              </div>
            </>
          )}

          <img
            src={activeImg}
            alt={product.name}
            className="max-h-full max-w-full object-contain select-none shadow-2xl rounded-sm"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}

function ProductDetailSkeleton({ id }: { id: string }) {
  return (
    <div className="section-container pb-32 pt-24 sm:pt-28 md:pt-32 animate-pulse min-h-screen">
      {/* Breadcrumb Skeleton */}
      <div className="mb-6 flex items-center gap-2">
        <div className="h-4 w-12 bg-stone-200 dark:bg-stone-800 rounded" />
        <span className="text-stone-300 dark:text-stone-700">/</span>
        <div className="h-4 w-16 bg-stone-200 dark:bg-stone-800 rounded" />
        <span className="text-stone-300 dark:text-stone-700">/</span>
        <div className="h-4 w-32 bg-stone-200 dark:bg-stone-800 rounded" />
      </div>

      <div className="grid gap-6 lg:grid-cols-12 lg:gap-10 items-start">
        {/* Left: Gallery Skeleton */}
        <div className="lg:col-span-7 min-w-0">
          <div className="flex gap-3 md:gap-4 items-start">
            {/* Thumbnails */}
            <div className="flex flex-col gap-2 shrink-0 w-12 sm:w-14 md:w-16 lg:w-20">
              {[1, 2, 3, 4].map((i) => (
                <div
                  key={i}
                  className="aspect-[4/5] w-full rounded-xl bg-stone-200/80 dark:bg-stone-800/80 border border-stone-200/60 dark:border-stone-700/60"
                />
              ))}
            </div>
            {/* Main Image Box */}
            <div className="relative h-[250px] sm:h-[350px] md:h-[460px] flex-1 w-full rounded-2xl bg-stone-200/80 dark:bg-stone-800/80 border border-stone-200/60 dark:border-stone-700/60" />
          </div>
        </div>

        {/* Right: Buy Column Skeleton */}
        <div className="lg:col-span-5 min-w-0 space-y-4">
          <div className="h-4 w-28 bg-stone-200 dark:bg-stone-800 rounded-full" />
          <div className="h-8 sm:h-10 w-3/4 bg-stone-200 dark:bg-stone-800 rounded-lg" />
          <div className="flex items-center justify-between border-b border-stone-200/80 dark:border-stone-800 pb-4">
            <div className="h-7 w-24 bg-stone-200 dark:bg-stone-800 rounded-md" />
            <div className="h-4 w-28 bg-stone-200 dark:bg-stone-800 rounded-md" />
          </div>

          {/* Action buttons skeleton */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 pt-2">
            <div className="h-12 w-28 bg-stone-200 dark:bg-stone-800 rounded-xl" />
            <div className="h-12 flex-1 bg-stone-200 dark:bg-stone-800 rounded-xl" />
            <div className="h-12 flex-1 bg-stone-200 dark:bg-stone-800 rounded-xl" />
          </div>

          {/* Accordions placeholder skeleton */}
          <div className="space-y-3 pt-4 border-b border-stone-200/80 dark:border-stone-800 pb-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex justify-between items-center py-2">
                <div className="h-4 w-40 bg-stone-200/80 dark:bg-stone-800/80 rounded" />
                <div className="h-4 w-4 bg-stone-200/80 dark:bg-stone-800/80 rounded" />
              </div>
            ))}
          </div>

          {/* Description skeleton */}
          <div className="space-y-2 pt-2">
            <div className="h-4 w-32 bg-stone-200 dark:bg-stone-800 rounded mb-3" />
            <div className="h-4 w-full bg-stone-200/70 dark:bg-stone-800/70 rounded" />
            <div className="h-4 w-5/6 bg-stone-200/70 dark:bg-stone-800/70 rounded" />
            <div className="h-4 w-4/6 bg-stone-200/70 dark:bg-stone-800/70 rounded" />
          </div>
        </div>
      </div>

      {/* Related Products Skeleton */}
      <div className="mt-20 pt-10 border-t border-stone-200/60 dark:border-stone-800/60">
        <div className="h-4 w-28 bg-stone-200 dark:bg-stone-800 rounded mb-3" />
        <div className="h-8 w-48 bg-stone-200 dark:bg-stone-800 rounded-lg mb-8" />
        <div className="grid gap-x-4 md:gap-x-6 gap-y-8 grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="space-y-3">
              <div className="aspect-[4/5] w-full rounded-2xl bg-stone-200/80 dark:bg-stone-800/80 border border-stone-200/60 dark:border-stone-700/60" />
              <div className="h-4 w-3/4 bg-stone-200 dark:bg-stone-800 rounded" />
              <div className="h-4 w-1/3 bg-stone-200 dark:bg-stone-800 rounded" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function ProductNotFound({ id }: { id: string }) {
  const formattedName = decodeURIComponent(id)
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());

  return (
    <div className="section-container pb-32 pt-24 sm:pt-28 md:pt-32">
      <div className="min-h-[50vh] flex flex-col items-center justify-center text-center px-4">
        <div className="grid h-16 w-16 place-items-center rounded-2xl bg-stone-100 dark:bg-stone-800/80 border border-stone-200 dark:border-stone-700/60 mb-4">
          <AlertCircle className="h-8 w-8 text-stone-500" />
        </div>
        <h1 className="font-display text-2xl sm:text-3xl font-bold text-foreground">
          Product Not Found
        </h1>
        <p className="mt-2 text-sm sm:text-base text-muted-foreground max-w-md">
          We couldn&apos;t find &quot;{formattedName}&quot;. It may have been relocated or is
          temporarily unavailable.
        </p>
        <Link
          to="/shop"
          className="mt-6 inline-flex items-center justify-center px-6 py-3 rounded-xl bg-emerald-800 hover:bg-emerald-700 text-white font-bold text-sm tracking-wider uppercase transition-colors shadow-sm"
        >
          Explore All Products
        </Link>
      </div>
    </div>
  );
}
